import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { HelpCircle, Play, ChevronRight, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";

interface Tour {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  estimatedMinutes: number;
  completedSteps?: number;
  totalSteps?: number;
}

interface ToursByCategory {
  [category: string]: Tour[];
}

const categoryLabels: { [key: string]: string } = {
  goals: "Goals & Milestones",
  tasks: "Tasks & Projects",
  habits: "Habit Tracking",
  calendar: "Calendar & Events",
  journal: "Journaling",
  notepad: "Notes & Ideas",
  planner: "Planning & Scheduling",
  media: "Media Library",
  dashboard: "Dashboard & Widgets",
  achievements: "Achievements",
  settings: "Settings & Preferences",
  general: "Getting Started",
};

const difficultyColors: { [key: string]: string } = {
  beginner: "bg-green-500/10 text-green-500 dark:bg-green-500/20",
  intermediate: "bg-yellow-500/10 text-yellow-500 dark:bg-yellow-500/20",
  advanced: "bg-red-500/10 text-red-500 dark:bg-red-500/20",
};

interface TourLauncherProps {
  variant?: "icon" | "text" | "inline";
  className?: string;
}

export default function TourLauncher({ variant = "icon", className = "" }: TourLauncherProps) {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { isAdmin } = useAuth();

  const { data: tours = [], isLoading } = useQuery<Tour[]>({
    queryKey: ["/api/tours"],
    enabled: open,
  });

  const toursByCategory: ToursByCategory = tours.reduce((acc, tour) => {
    const category = tour.category || "general";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tour);
    return acc;
  }, {} as ToursByCategory);

  const allTours = tours.filter(t => t.totalSteps && t.totalSteps > 0);

  const handleStartTour = (tourId: string) => {
    setOpen(false);
    setLocation(`/tour/${tourId}`);
  };

  const handleStartAllTours = () => {
    if (allTours.length > 0) {
      setOpen(false);
      setLocation(`/tour/${allTours[0].id}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "inline" ? (
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${className}`}
            data-testid="button-tour-launcher"
            title="Guided Tours"
            onClick={(e) => e.stopPropagation()}
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        ) : variant === "icon" ? (
          <Button
            variant="ghost"
            size="icon"
            className={className}
            data-testid="button-tour-launcher"
            title="Guided Tours"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            variant="outline"
            className={className}
            data-testid="button-tour-launcher"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Help
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-tour-menu">
        <DialogHeader>
          <DialogTitle>Guided Tours</DialogTitle>
          <DialogDescription>
            Choose a tour to learn about specific features or start from the beginning
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Complete Tour Option */}
            {allTours.length > 0 && (
              <div className="pb-4 border-b">
                <Button
                  onClick={handleStartAllTours}
                  className="w-full h-auto py-4 justify-start"
                  variant="default"
                  data-testid="button-start-complete-tour"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center flex-shrink-0">
                      <Play className="h-5 w-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold">Full Tour</div>
                      <div className="text-sm opacity-90">
                        Take the full guided tour from beginning to end
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 opacity-50" />
                  </div>
                </Button>
              </div>
            )}

            {/* Tours by Category */}
            {isLoading ? (
              <div className="text-center text-muted-foreground py-8">
                Loading tours...
              </div>
            ) : Object.keys(toursByCategory).length === 0 ? (
              <div className="text-center py-8 space-y-4">
                <div className="text-muted-foreground">
                  {isAdmin 
                    ? "No guided tours have been created yet." 
                    : "Guided tours coming soon!"}
                </div>
                {isAdmin && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpen(false);
                      setLocation("/admin/tours");
                    }}
                    data-testid="button-create-first-tour"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Create Your First Tour
                  </Button>
                )}
              </div>
            ) : (
              Object.entries(toursByCategory).map(([category, categoryTours]) => (
                <div key={category} className="space-y-3">
                  <h3 className="font-semibold text-lg">
                    {categoryLabels[category] || category}
                  </h3>
                  <div className="space-y-2">
                    {categoryTours.map((tour) => (
                      <button
                        key={tour.id}
                        onClick={() => handleStartTour(tour.id)}
                        className="w-full text-left p-4 rounded-lg border hover:bg-accent transition-colors"
                        data-testid={`button-tour-${tour.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="font-medium mb-1">{tour.title}</div>
                            <div className="text-sm text-muted-foreground mb-2">
                              {tour.description}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="outline"
                                className={difficultyColors[tour.difficulty] || ""}
                              >
                                {tour.difficulty}
                              </Badge>
                              <Badge variant="outline">
                                {tour.estimatedMinutes} min
                              </Badge>
                              {tour.totalSteps && (
                                <Badge variant="outline">
                                  {tour.totalSteps} steps
                                </Badge>
                              )}
                              {tour.completedSteps !== undefined &&
                                tour.totalSteps !== undefined &&
                                tour.completedSteps > 0 && (
                                  <Badge variant="outline" className="bg-green-500/10 text-green-500">
                                    {Math.round(
                                      (tour.completedSteps / tour.totalSteps) * 100
                                    )}
                                    % complete
                                  </Badge>
                                )}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
